/**
 * MeineDokumentePage.jsx – Mitarbeiter-Sicht für eigene Dokumente
 * Route: /employee/dokumente
 *
 * Zeigt alle Dokumente des eingeloggten Mitarbeiters, gruppiert nach Ordner.
 * Ausstehende Unterschriften oben prominent. Fingerunterschrift-Flow via SignaturePad.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Folder, FolderOpen, CheckCircle2, Clock, Eye,
  Download, PenLine, ChevronRight, ChevronDown, Loader2, X, Check,
} from 'lucide-react';
import SignaturePad from '../../components/ui/SignaturePad';
import { PdfViewer } from '../../components/PdfViewer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function buildTree(folders) {
  const map = {};
  const roots = [];
  folders.forEach(f => { map[f.id] = { ...f, children: [] }; });
  folders.forEach(f => {
    if (f.parent_folder_id && map[f.parent_folder_id]) {
      map[f.parent_folder_id].children.push(map[f.id]);
    } else {
      roots.push(map[f.id]);
    }
  });
  return roots;
}

// ── Signatur-Modal ─────────────────────────────────────────────────────────────
function SignaturModal({ doc, onClose, onSigned }) {
  const sigPadRef = useRef(null);
  const [signing, setSigning] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [viewLoading, setViewLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setViewLoading(true);
        const r = await axios.get(`${API}/employee-documents/${doc.id}/file`, {
          headers: authH(), responseType: 'blob',
        });
        setPreviewUrl({ url: URL.createObjectURL(r.data), type: r.data.type });
      } catch { /* preview optional */ }
      finally { setViewLoading(false); }
    })();
    return () => { if (previewUrl?.url) URL.revokeObjectURL(previewUrl.url); };
  }, [doc.id]); // eslint-disable-line

  const handleSubmit = async () => {
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      toast.error('Bitte zuerst unterschreiben');
      return;
    }
    setSigning(true);
    try {
      const sigData = sigPadRef.current.toDataURL('image/png');
      await axios.post(`${API}/employee-documents/${doc.id}/confirm`,
        { signature_employee: sigData },
        { headers: authH() }
      );
      toast.success('Dokument bestätigt und unterschrieben');
      onSigned();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Unterschreiben'); }
    finally { setSigning(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex flex-col" onClick={onClose}>
      <div className="bg-card w-full max-w-lg mx-auto mt-8 rounded-2xl shadow-2xl flex flex-col max-h-[92dvh] overflow-hidden"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="font-bold text-sm">Dokument unterschreiben</h2>
            <p className="text-xs text-muted-foreground truncate max-w-[240px]">{doc.file_name}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-muted text-muted-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Dokument-Vorschau */}
        <div className="flex-1 overflow-auto min-h-[200px] bg-muted/30">
          {viewLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : previewUrl ? (
            previewUrl.type?.startsWith('image/') ? (
              <img src={previewUrl.url} alt={doc.file_name} className="max-w-full max-h-64 mx-auto object-contain p-4" />
            ) : (
              <PdfViewer url={previewUrl.url} className="max-h-64 overflow-auto" />
            )
          ) : (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <FileText className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-xs">Vorschau nicht verfügbar</p>
            </div>
          )}
        </div>

        {/* Signatur-Bereich */}
        <div className="px-5 py-4 border-t space-y-3">
          <p className="text-xs font-medium text-muted-foreground">
            Mit deiner Unterschrift bestätigst du, das Dokument gelesen und zur Kenntnis genommen zu haben.
          </p>
          <div className="border rounded-xl overflow-hidden bg-background">
            <SignaturePad
              ref={sigPadRef}
              canvasProps={{ className: 'w-full h-36', style: { touchAction: 'none' } }}
              penColor="#1e3a5f"
            />
          </div>
          <p className="text-[10px] text-muted-foreground text-center">
            Hier mit dem Finger oder der Maus unterschreiben
          </p>
        </div>

        {/* Footer Buttons */}
        <div className="flex gap-2 px-5 pb-5">
          <Button variant="outline" className="flex-1" onClick={() => sigPadRef.current?.clear()}>
            Löschen
          </Button>
          <Button className="flex-1 gap-1.5" onClick={handleSubmit} disabled={signing}
            data-testid="submit-signature-btn">
            {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Unterschreiben & Bestätigen
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Dokument-Karte ─────────────────────────────────────────────────────────────
function DocCard({ doc, onSign, onView }) {
  const confirmed = !!doc.confirmed_at;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
      doc.require_signature && !confirmed ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/10' : 'border-border bg-muted/20'
    }`} data-testid={`my-doc-${doc.id}`}>
      <FileText className={`h-8 w-8 shrink-0 ${doc.require_signature && !confirmed ? 'text-amber-600' : 'text-muted-foreground'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{doc.file_name}</p>
        <p className="text-xs text-muted-foreground">
          {new Date(doc.uploaded_at).toLocaleDateString('de-DE')} · {doc.folder_name}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {doc.require_signature && (
          confirmed ? (
            <Badge variant="outline" className="text-[10px] text-green-700 border-green-300">
              <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Unterschrieben
            </Badge>
          ) : (
            <Button size="sm" onClick={() => onSign(doc)} className="h-8 text-xs gap-1"
              data-testid={`sign-doc-btn-${doc.id}`}>
              <PenLine className="h-3 w-3" /> Unterschreiben
            </Button>
          )
        )}
        <button onClick={() => onView(doc)} title="Ansehen"
          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <Eye className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ── Hauptseite ─────────────────────────────────────────────────────────────────
export default function MeineDokumentePage() {
  const [data, setData] = useState({ folders: [], documents: [] });
  const [loading, setLoading] = useState(true);
  const [signDoc, setSignDoc] = useState(null);
  const [viewUrl, setViewUrl] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // employeeId ist die eigene ID → wird im Backend aus dem Token ermittelt
      // Wir nutzen /pending für pendiente + alle docs via /by-employee/{eigene_id}
      const meRes = await axios.get(`${API}/auth/me`, { headers: authH() });
      const myId = meRes.data?.id;
      if (!myId) return;
      const r = await axios.get(`${API}/employee-documents/by-employee/${myId}`, { headers: authH() });
      setData(r.data || { folders: [], documents: [] });
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleView = async (doc) => {
    try {
      const r = await axios.get(`${API}/employee-documents/${doc.id}/file`, {
        headers: authH(), responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      setViewUrl({ url, name: doc.file_name, type: r.data.type });
    } catch { toast.error('Datei nicht verfügbar'); }
  };

  const pending = data.documents.filter(d => d.require_signature && !d.confirmed_at);
  const signed  = data.documents.filter(d => d.require_signature && !!d.confirmed_at);
  const noBadge = data.documents.filter(d => !d.require_signature);

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto p-4 pb-24 space-y-4" data-testid="meine-dokumente-page">
        <div>
          <h1 className="text-2xl font-bold">Meine Dokumente</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Vom Arbeitgeber bereitgestellte Nachweise und Zertifikate
          </p>
        </div>

        {/* Ausstehende Unterschriften */}
        {pending.length > 0 && (
          <Card className="border-amber-300 bg-amber-50 dark:bg-amber-900/10">
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-800 dark:text-amber-300">
                <Clock className="h-4 w-4" />
                {pending.length} Dokument{pending.length > 1 ? 'e' : ''} zur Unterschrift ausstehend
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {pending.map(doc => (
                <DocCard key={doc.id} doc={doc}
                  onSign={setSignDoc}
                  onView={handleView} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Unterschriebene Dokumente */}
        {signed.length > 0 && (
          <Card>
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" /> Unterschriebene Dokumente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {signed.map(doc => (
                <DocCard key={doc.id} doc={doc} onSign={setSignDoc} onView={handleView} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Sonstige Dokumente */}
        {noBadge.length > 0 && (
          <Card>
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-sm font-semibold">Weitere Dokumente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {noBadge.map(doc => (
                <DocCard key={doc.id} doc={doc} onSign={setSignDoc} onView={handleView} />
              ))}
            </CardContent>
          </Card>
        )}

        {data.documents.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">Noch keine Dokumente vorhanden</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Signatur-Modal */}
      {signDoc && (
        <SignaturModal
          doc={signDoc}
          onClose={() => setSignDoc(null)}
          onSigned={() => { setSignDoc(null); fetchData(); }}
        />
      )}

      {/* View Modal */}
      {viewUrl && (
        <div className="fixed inset-0 bg-black/80 z-50 flex flex-col"
          onClick={() => { URL.revokeObjectURL(viewUrl.url); setViewUrl(null); }}>
          <div className="flex items-center justify-between px-4 py-3 bg-black/50 text-white"
            onClick={e => e.stopPropagation()}>
            <span className="text-sm font-medium truncate">{viewUrl.name}</span>
            <div className="flex items-center gap-2">
              <a href={viewUrl.url} download={viewUrl.name}
                className="flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg"
                onClick={e => e.stopPropagation()}>
                <Download className="h-3.5 w-3.5" /> Download
              </a>
              <button className="text-white/60 hover:text-white px-2"
                onClick={() => { URL.revokeObjectURL(viewUrl.url); setViewUrl(null); }}>✕</button>
            </div>
          </div>
          <div className="flex-1 overflow-auto" onClick={e => e.stopPropagation()}>
            {viewUrl.type?.startsWith('image/') ? (
              <img src={viewUrl.url} alt={viewUrl.name} className="max-w-full h-auto mx-auto object-contain p-4" />
            ) : (
              <PdfViewer url={viewUrl.url} />
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
